<template>
  <div class="paa-card paa-rates">
    <div class="paa-rates__head">
      <b>Fixed commission per paid invoice</b>
      <span class="paa-muted">
        From Packages &amp; Costings: SEO Manager hours x {{ money(hourly) }}/hr. Applied to every paid package month.
      </span>
    </div>
    <div v-if="loadError" class="paa-error">{{ loadError }}</div>
    <div v-else-if="loading" class="paa-muted">Loading…</div>
    <template v-else>
      <div class="paa-rates__grid">
        <label v-for="field in fields" :key="field.key" class="paa-rf">
          <span>{{ field.label }}</span>
          <span class="paa-rf__in">
            <template v-if="field.unit !== 'percent'">$</template>
            <input
              v-model="draft[field.key]"
              type="number"
              step="0.5"
              min="0"
              :disabled="!canEdit || saving"
              @keydown.enter.prevent="save"
            />
            <template v-if="field.unit === 'percent'">%</template>
          </span>
          <small>{{ field.note }}</small>
        </label>
      </div>
      <div class="paa-rates__foot">
        <button v-if="canEdit" type="button" class="paa-btn paa-btn--primary paa-btn--sm" :disabled="saving" @click="save">Save rates</button>
        <span class="paa-muted">
          Changes apply to invoices paid from today. Past commissions keep the rate that was set when they were paid.
        </span>
      </div>
    </template>
  </div>
</template>

<script>
import { showPopAuthorityToast } from '@/utils/popAuthority'
import { paAdminMoney } from './paAdminConstants'

/**
 * "Edit fixed commission rates" (design ratesPanel): one amount per package month, per SEO
 * Manager hour of a custom package, per setup fee and per booster pack, and the percentage of a
 * content only order. Read only without edit access to the commissions dashboard.
 */
export default {
  name: 'AdminPopAuthorityCommissionRatesPanel',
  data () {
    return {
      loading: true,
      saving: false,
      loadError: '',
      fields: [],
      draft: {},
      canEdit: false,
    }
  },
  computed: {
    hourly () {
      return this.draft.custom_hourly
    },
  },
  created () {
    this.load()
  },
  methods: {
    money: paAdminMoney,
    apply (data) {
      this.fields = data.fields || []
      this.canEdit = Boolean(data.canEdit)
      const draft = {}
      this.fields.forEach((field) => {
        draft[field.key] = (data.rates || {})[field.key]
      })
      this.draft = draft
    },
    load () {
      this.loading = true
      this.loadError = ''
      return this.$store.dispatch('getPopAuthorityCommissionRates')
        .then((response) => {
          const data = response && response.data
          if (!data || data.status !== 'SUCCESS') throw new Error((data && data.msg) || 'failed')
          this.apply(data)
        })
        .catch(() => {
          this.loadError = 'We could not load the commission rates. Try again in a moment.'
        })
        .finally(() => {
          this.loading = false
        })
    },
    save () {
      if (!this.canEdit || this.saving) return null
      const rates = {}
      for (const field of this.fields) {
        const value = parseFloat(this.draft[field.key])
        if (!Number.isFinite(value) || value < 0) {
          this.$notify({ group: 'info', type: 'error', text: `${field.label}: enter an amount of 0 or more` })
          return null
        }
        rates[field.key] = value
      }
      this.saving = true
      return this.$store.dispatch('updatePopAuthorityCommissionRates', { body: { rates } })
        .then((response) => {
          const data = response && response.data
          if (!data || data.status !== 'SUCCESS') {
            this.$notify({ group: 'info', type: 'error', text: (data && data.msg) || 'The rates could not be saved.' })
            return
          }
          this.apply(data)
          showPopAuthorityToast(this, 'Commission rates saved')
          this.$emit('saved')
        })
        .catch((error) => {
          const msg = error && error.response && error.response.data && error.response.data.msg
          this.$notify({ group: 'info', type: 'error', text: msg || 'The rates could not be saved.' })
        })
        .finally(() => {
          this.saving = false
        })
    },
  },
}
</script>
